import React from 'react';
import { X, Shield, Lock, Eye, Database, Users, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PrivacyPolicyProps {
  onClose?: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onClose }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // If no onClose prop, navigate back or to auth page
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/auth');
      }
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Privacy Policy</h2>
              <p className="text-sm text-gray-500">Grit, Growth & Gratitude Platform</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Last Updated */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              1. Introduction
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                At Grit, Growth & Gratitude (GGG), we are committed to protecting your privacy and personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
              <p>
                We understand the sensitive nature of mental health information and are dedicated to maintaining the highest 
                standards of privacy and security for first responders.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Database className="w-5 h-5 mr-2 text-blue-600" />
              2. Information We Collect
            </h3>
            <div className="text-gray-700 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Personal Information</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Name and contact information</li>
                  <li>Email address and password</li>
                  <li>First responder type and agency affiliation</li>
                  <li>Department access codes (if applicable)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Wellness Data</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Journal entries and reflections</li>
                  <li>Training module progress and completion</li>
                  <li>Quiz responses and scores</li>
                  <li>Wellness assessments and mood tracking</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Technical Information</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Device information and browser type</li>
                  <li>IP address and location data</li>
                  <li>Usage patterns and platform interactions</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Eye className="w-5 h-5 mr-2 text-blue-600" />
              3. How We Use Your Information
            </h3>
            <div className="text-gray-700 space-y-3">
              <p><strong>We use your information to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and improve our wellness platform services</li>
                <li>Track your progress and personalize your experience</li>
                <li>Send important updates and notifications</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
                <li>Generate anonymized analytics and insights</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Lock className="w-5 h-5 mr-2 text-blue-600" />
              4. Data Security
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication</li>
                <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                <li><strong>Secure Infrastructure:</strong> Cloud-based security with enterprise-grade protection</li>
                <li><strong>Staff Training:</strong> Regular privacy and security training for all staff</li>
              </ul>
            </div>
          </section>

          {/* Information Sharing */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              5. Information Sharing
            </h3>
            <div className="text-gray-700 space-y-3">
              <p><strong>We do not sell, trade, or rent your personal information to third parties. We may share information only in these limited circumstances:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
                <li><strong>Legal Requirements:</strong> When required by law or court order</li>
                <li><strong>Safety Emergencies:</strong> To protect your safety or the safety of others</li>
                <li><strong>Service Providers:</strong> With trusted partners who help us operate our platform (under strict confidentiality agreements)</li>
                <li><strong>Anonymized Data:</strong> Aggregated, non-identifiable data for research and improvement</li>
              </ul>
            </div>
          </section>

          {/* Mental Health Data Protection */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-blue-600" />
              6. Mental Health Data Protection
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                <strong>Special Protections:</strong> We recognize the sensitive nature of mental health information and provide enhanced protections:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Journal entries are encrypted with additional security layers</li>
                <li>Access to mental health data is strictly limited to essential personnel</li>
                <li>No sharing of personal mental health information without explicit consent</li>
                <li>Regular security audits specifically for mental health data</li>
                <li>Compliance with HIPAA and other relevant privacy regulations</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Your Rights</h3>
            <div className="text-gray-700 space-y-3">
              <p><strong>You have the right to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Restriction:</strong> Limit how we process your information</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
              </ul>
              <p>
                To exercise these rights, contact us at privacy@gritgrowthgratitude.com
              </p>
            </div>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                We use cookies and similar technologies to enhance your experience and analyze platform usage. 
                You can control cookie settings through your browser preferences.
              </p>
              <p>
                <strong>Types of cookies we use:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Essential cookies for platform functionality</li>
                <li>Analytics cookies for usage insights</li>
                <li>Preference cookies for personalization</li>
              </ul>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Data Retention</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                We retain your information only as long as necessary to provide our services and comply with legal obligations. 
                Generally, we retain:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Account information: Until account deletion</li>
                <li>Journal entries: Until you delete them or close your account</li>
                <li>Training progress: For service improvement and compliance</li>
                <li>Analytics data: In anonymized form for up to 2 years</li>
              </ul>
            </div>
          </section>

          {/* Children's Privacy */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Children's Privacy</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                Our platform is not intended for children under 18. We do not knowingly collect personal information from children. 
                If we become aware that we have collected information from a child, we will take steps to delete such information.
              </p>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">11. Changes to This Privacy Policy</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through our platform. 
                Your continued use of our services after such changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">12. Contact Information</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p><strong>Privacy Officer:</strong> privacy@gritgrowthgratitude.com</p>
                <p><strong>Data Protection:</strong> dpo@gritgrowthgratitude.com</p>
                <p><strong>General Support:</strong> support@gritgrowthgratitude.com</p>
                <p><strong>Address:</strong> [Your Business Address]</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Your privacy is important to us. This policy explains how we protect your information.
            </p>
            <button
              onClick={handleClose}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};















